import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';

import { ID } from '../../../../../../../types';

import { FetchLifestyleDetection } from '../../../../../../lifestyleDetections/queries/lifestyleDetectionsByImageUrl.query';

import { ThreeDStockContentImageLensLoadingOverlay } from '../../../ThreeDStockContentImageLens/components/ThreeDStockContentImageLensLoadingOverlay';

import { PureIconButtonHelper } from '../../../../../../../helpers/buttons/PureIconButtonHelper';

import { IconsEnum } from '../../../../../../../assets/icons/types';

interface ThreeDStockContentSearchInImageAllObjectsProps {
  imageUrl: string;
  activeDetectionId?: ID | null;
  onActiveDetectionChange?: (id: ID | null) => void;
  detections?: FetchLifestyleDetection[];
  isLoading?: boolean;
  onClearSearch?: () => void;
}

function ThreeDStockContentSearchInImageAllObjects({
  imageUrl,
  detections,
  isLoading = false,
  onClearSearch,
  activeDetectionId,
  onActiveDetectionChange
}: ThreeDStockContentSearchInImageAllObjectsProps) {
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });

  const orderedDetections = useMemo(() => {
    // Render smaller frames last so larger frames cannot intercept their hover.
    return detections
      ?.map((detection, index) => ({ detection, index }))
      .sort((a, b) => {
        return (
          b.detection.width * b.detection.height -
          a.detection.width * a.detection.height
        );
      });
  }, [detections]);

  const handleImageLoad = useCallback(
    ({ currentTarget }: SyntheticEvent<HTMLImageElement>) => {
      setImageSize({
        width: currentTarget.naturalWidth,
        height: currentTarget.naturalHeight
      });
    },
    []
  );

  return (
    <div className="relative overflow-hidden w-full h-[50vh] rounded-lg flex items-center justify-center p-1 bg-gray-100 dark:bg-gray-850 border dark:border-gray-700">
      <div className="relative max-w-full w-fit overflow-hidden rounded">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={imageUrl}
          alt="Detected objects"
          onLoad={handleImageLoad}
          draggable={false}
          className="block max-h-[calc(50vh-0.5rem)] w-auto max-w-full rounded select-none"
        />
        {!isLoading &&
          imageSize.width > 0 &&
          imageSize.height > 0 &&
          orderedDetections?.map(({ detection, index }) => (
            <div
              key={detection.id}
              role="img"
              tabIndex={0}
              aria-label={`Object ${index + 1}: ${detection.category}`}
              onMouseEnter={() => onActiveDetectionChange?.(detection.id)}
              onMouseLeave={() => onActiveDetectionChange?.(null)}
              onFocus={() => onActiveDetectionChange?.(detection.id)}
              onBlur={() => onActiveDetectionChange?.(null)}
              className={classNames(
                'group/frame absolute border-2 rounded-md shadow-[0_0_0_1px_rgb(0_0_0/0.25)] hover:border-blue-600 focus-visible:border-blue-600 hover:bg-blue-500/[0.08] focus-visible:bg-blue-500/[0.08]',
                activeDetectionId === detection.id
                  ? 'border-blue-600 bg-blue-500/[0.08]'
                  : 'border-white bg-transparent'
              )}
              style={{
                left: `${(detection.x1 / imageSize.width) * 100}%`,
                top: `${(detection.y1 / imageSize.height) * 100}%`,
                width: `${(detection.width / imageSize.width) * 100}%`,
                height: `${(detection.height / imageSize.height) * 100}%`
              }}
            >
              <span
                className={classNames(
                  'absolute -top-3 -left-0.5 text-[12px] leading-[24px] min-w-[24px] px-[6px] rounded-[5px] whitespace-nowrap group-hover/frame:bg-blue-600 group-hover/frame:text-white group-focus-visible/frame:bg-blue-600 group-focus-visible/frame:text-white',
                  activeDetectionId === detection.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-900'
                )}
              >
                {index + 1}
                <span
                  className={classNames(
                    'group-hover/frame:inline group-focus-visible/frame:inline',
                    activeDetectionId === detection.id ? 'inline' : 'hidden'
                  )}
                >
                  {' · '}
                  {detection.category}
                </span>
              </span>
            </div>
          ))}
        {isLoading && <ThreeDStockContentImageLensLoadingOverlay />}
      </div>
      {onClearSearch && (
        <PureIconButtonHelper
          className="absolute top-2 right-2 dark:hover:bg-gray-700 dark:hover:text-gray-200 dark:text-gray-500 hover:bg-gray-200 hover:text-gray-600 p-2 rounded-full text-gray-400"
          icon={IconsEnum.X}
          iconClassName="h-3.5 w-3.5"
          onClick={onClearSearch}
        />
      )}
    </div>
  );
}

export default ThreeDStockContentSearchInImageAllObjects;
